import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { audioManager } from "../utils/audioManager";

export interface UseAudioRecorder {
  isRecording: boolean;
  isPaused: boolean;
  durationMs: number;
  durationLabel: string;
  audioBlob?: Blob;
  start: () => Promise<void>;
  stop: () => Promise<Blob | undefined>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  restart: () => Promise<void>;
  cancel: () => Promise<void>;
  cancelRecording: () => Promise<void>;
  reset: () => void;
  getPreviewUrl: () => string | undefined;
  // UI helpers
  toggleMic: (opts?: { disabled?: boolean }) => Promise<void>;
  isPreviewPlaying: boolean;
  togglePreview: () => Promise<void>;
  discardRecording: () => void;
}

const MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/ogg",
  "audio/mp4",
];

function pickSupportedMimeType(): string | undefined {
  // Prefer opus/webm where possible for smaller size
  return MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t));
}

const useAudioRecorder = (): UseAudioRecorder => {
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const intervalRef = useRef<number | null>(null);
  const cancelRequestedRef = useRef<boolean>(false);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const onStopResolveRef = useRef<((blob?: Blob) => void) | null>(null);
  const latestBlobRef = useRef<Blob | undefined>(undefined);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | undefined>(undefined);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);

  const RECORDINGCAP_MS = 60000; // 60 seconds

  const clearTimer = () => {
    if (intervalRef.current != null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const tickStart = useCallback(() => {
    clearTimer();
    setDurationMs(0);
    intervalRef.current = window.setInterval(() => {
      setDurationMs((d) => {
        const next = d + 1000;
        return next > RECORDINGCAP_MS ? RECORDINGCAP_MS : next;
      });
    }, 1000);
  }, []);

  const tickResume = useCallback(() => {
    clearTimer();
    intervalRef.current = window.setInterval(() => {
      setDurationMs((d) => {
        const next = d + 1000;
        return next > RECORDINGCAP_MS ? RECORDINGCAP_MS : next;
      });
    }, 1000);
  }, []);

  const releaseMedia = () => {
    mediaRecorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    mediaRecorderRef.current = null;
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
  };

  const reset = useCallback(() => {
    clearTimer();
    setIsRecording(false);
    setIsPaused(false);
    setDurationMs(0);
    setAudioBlob(undefined);
    chunksRef.current = [];
    // do not stop devices here; they are stopped in stop()
  }, []);

  const start = useCallback(async () => {
    // guard by actual recorder state instead of React state to avoid stale closures
    const state = mediaRecorderRef.current?.state;
    if (state === "recording" || state === "paused") return;
    // ask permission and start recording
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStreamRef.current = stream;

    const mimeType = pickSupportedMimeType();
    const recorder = new MediaRecorder(
      stream,
      mimeType ? { mimeType } : undefined,
    );
    mediaRecorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      let blob;
      if (!cancelRequestedRef.current) {
        blob = new Blob(chunksRef.current, {
          type: recorder.mimeType ?? mimeType ?? "audio/webm",
        });
        latestBlobRef.current = blob;
        setAudioBlob(blob);
      }
      chunksRef.current = [];
      clearTimer();
      setIsRecording(false);
      setIsPaused(false);
      cancelRequestedRef.current = false;
      // keep stream stopped after stop()
      releaseMedia();
      // resolve any waiter with the blob
      if (onStopResolveRef.current) {
        const resolve = onStopResolveRef.current;
        onStopResolveRef.current = null;
        resolve(blob);
      }
    };

    recorder.start();
    setIsRecording(true);
    tickStart();
  }, [tickStart]);

  const stop = useCallback(async (): Promise<Blob | undefined> => {
    if (!isRecording) return undefined;

    // promise that resolves with the blob when onstop completes
    const waitForStop = new Promise<Blob | undefined>((resolve) => {
      onStopResolveRef.current = resolve;
    });

    mediaRecorderRef.current?.stop();

    // wait for blob to be created and return it
    return waitForStop;
  }, [isRecording]);

  const pause = useCallback(async () => {
    if (!isRecording) return;
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      clearTimer();
    }
  }, [isRecording]);

  const resume = useCallback(async () => {
    if (!isRecording) return;
    if (mediaRecorderRef.current?.state === "paused") {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      tickResume();
    }
  }, [isRecording, tickResume]);

  const cancel = useCallback(async () => {
    if (!isRecording) return;
    // indicate canceling so onstop does not create a blob
    cancelRequestedRef.current = true;
    const waitForStop = new Promise<void>((resolve) => {
      onStopResolveRef.current = () => resolve();
    });
    mediaRecorderRef.current?.stop();
    await waitForStop;
  }, [isRecording]);

  const cancelRecording = useCallback(async () => {
    await cancel();
    // stop and cleanup preview if any
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
      setIsPreviewPlaying(false);
    }
    reset();
  }, [cancel, reset]);

  const restart = useCallback(async () => {
    await cancelRecording();
    await start();
  }, [cancelRecording, start]);

  const getPreviewUrl = useCallback(() => {
    if (!audioBlob) return undefined;
    return URL.createObjectURL(audioBlob);
  }, [audioBlob]);

  const toggleMic = useCallback(
    async (opts?: { disabled?: boolean }) => {
      if (opts?.disabled) return;
      if (isRecording) {
        await stop();
        return;
      }
      // stop any currently playing audio before recording
      audioManager.pause();
      await start();
    },
    [isRecording, start, stop],
  );

  const togglePreview = useCallback(async () => {
    if (!audioBlob) return;
    const url = getPreviewUrl();
    if (!url) return;
    if (!previewAudioRef.current) {
      previewAudioRef.current = new Audio(url);
      previewAudioRef.current.onended = () => setIsPreviewPlaying(false);
      previewAudioRef.current.onpause = () => setIsPreviewPlaying(false);
      previewAudioRef.current.onplay = () => setIsPreviewPlaying(true);
    }
    const a = previewAudioRef.current;
    if (isPreviewPlaying) {
      a.pause();
      return;
    }
    a.currentTime = 0;
    await a.play();
  }, [audioBlob, getPreviewUrl, isPreviewPlaying]);

  const discardRecording = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
      setIsPreviewPlaying(false);
    }
    reset();
  }, [reset]);

  const durationLabel = useMemo(() => {
    const total = Math.floor(durationMs / 1000);
    const mm = String(Math.floor(total / 60)).padStart(2, "0");
    const ss = String(total % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  }, [durationMs]);

  useEffect(() => {
    return () => {
      clearTimer();
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }
      releaseMedia();
    };
  }, []);

  return {
    isRecording,
    isPaused,
    durationMs,
    durationLabel,
    audioBlob,
    start,
    stop,
    pause,
    resume,
    restart,
    cancel,
    cancelRecording,
    reset,
    getPreviewUrl,
    toggleMic,
    isPreviewPlaying,
    togglePreview,
    discardRecording,
  };
};

export default useAudioRecorder;
